// 인게임 상단 우측 패널의 "남은 팀 수(N TEAMS) / 인원"을 읽는다. OpenCV 없이 순수 JS로 작은 영역만 처리한다.
//
// 입력은 기준 해상도(높이 1440) 격자의 패널 영역(420×120 RGBA, 가로 중앙 기준 오른쪽)이다.
// 낮/밤 전환 순간 패널이 가로로 밀리는 경우가 있어 고정 좌표 대신 "큰 흰 숫자 덩어리"를 찾아 읽는다.
// (TEAMS 글자, 사람 아이콘, ?의 점은 작아서 크기 필터로 걸러진다.)

export const STATUS_REGION = { x: 1860, y: 0, width: 420, height: 120 }; // 기준 해상도(높이 1440)의 원본 좌표
export const STATUS_CENTER_X = 1720; // 화면 가로 중앙(3440/2)

/**
 * 화면(video) 크기에서 패널 영역을 오려 올 원본 픽셀 위치. HUD는 가로 중앙 기준, 크기는 화면 높이 기준(높이 1440 = 배율 1)이다.
 * 오려 온 영역은 STATUS_REGION 크기(420×120)로 맞춰 읽는다.
 */
export function statusRegionRect(videoWidth, videoHeight) {
  const scale = videoHeight / 1440;
  return {
    sx: Math.round(videoWidth / 2 + (STATUS_REGION.x - STATUS_CENTER_X) * scale),
    sy: Math.round(STATUS_REGION.y * scale),
    sw: Math.round(STATUS_REGION.width * scale),
    sh: Math.round(STATUS_REGION.height * scale),
  };
}

const BAND = { y0: 20, y1: 58 }; // 숫자가 놓이는 세로 띠 (영역 안 좌표)
const WHITE_THRESHOLD = 185; // 글자(흰색)로 보는 밝기
const GLYPH_W = 14;
const GLYPH_H = 22;
const MAX_DISTANCE = 0.2; // 템플릿과 다른 픽셀 비율이 이보다 크면 못 읽은 것으로 본다
const MIN_MARGIN = 0.03; // 1등 값과 다른 값 중 가장 가까운 것의 격차가 이보다 작으면 단정하지 않는다

const luma = (data, i) => 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];

// 큰 흰 덩어리(숫자 후보)를 왼쪽부터 찾는다. 각 덩어리는 14×22 이진 비트열(bits)을 가진다.
export function extractGlyphs(image) {
  const { width, data } = image;
  const mask = new Uint8Array(width * image.height);
  for (let y = BAND.y0; y <= BAND.y1; y += 1) {
    for (let x = 0; x < width; x += 1) {
      mask[y * width + x] = luma(data, (y * width + x) * 4) >= WHITE_THRESHOLD ? 1 : 0;
    }
  }
  const seen = new Uint8Array(mask.length);
  const comps = [];
  for (let y = BAND.y0; y <= BAND.y1; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (!mask[y * width + x] || seen[y * width + x]) {
        continue; // eslint-disable-line no-continue
      }
      const stack = [[x, y]];
      seen[y * width + x] = 1;
      let [x0, x1, y0, y1, n] = [x, x, y, y, 0];
      while (stack.length) {
        const [cx, cy] = stack.pop();
        n += 1;
        x0 = Math.min(x0, cx);
        x1 = Math.max(x1, cx);
        y0 = Math.min(y0, cy);
        y1 = Math.max(y1, cy);
        [[1, 0], [-1, 0], [0, 1], [0, -1]].forEach(([dx, dy]) => {
          const nx = cx + dx;
          const ny = cy + dy;
          if (nx >= 0 && nx < width && ny >= BAND.y0 && ny <= BAND.y1 && mask[ny * width + nx] && !seen[ny * width + nx]) {
            seen[ny * width + nx] = 1;
            stack.push([nx, ny]);
          }
        });
      }
      comps.push({ x0, x1, y0, y1, n });
    }
  }
  return comps
    .filter((c) => {
      const w = c.x1 - c.x0 + 1;
      const h = c.y1 - c.y0 + 1;
      return h >= 14 && h <= 28 && w >= 5 && w <= 22 && c.n >= 40;
    })
    .sort((a, b) => a.x0 - b.x0)
    .map((c) => {
      const w = c.x1 - c.x0 + 1;
      const h = c.y1 - c.y0 + 1;
      let bits = '';
      for (let oy = 0; oy < GLYPH_H; oy += 1) {
        for (let ox = 0; ox < GLYPH_W; ox += 1) {
          const sx = c.x0 + Math.min(w - 1, Math.floor(((ox + 0.5) * w) / GLYPH_W));
          const sy = c.y0 + Math.min(h - 1, Math.floor(((oy + 0.5) * h) / GLYPH_H));
          bits += mask[sy * width + sx] ? '1' : '0';
        }
      }
      return { ...c, bits };
    });
}

const hamming = (a, b) => {
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      diff += 1;
    }
  }
  return diff / a.length;
};

// 글자 하나를 '0'~'9' 또는 '?'로 분류한다. 템플릿은 { 값: [비트열(변형)...] }. 못 정하면 null.
export function classifyGlyph(bits, templates) {
  const distances = Object.entries(templates)
    .map(([value, variants]) => ({ value, distance: Math.min(...variants.map((v) => hamming(bits, v))) }))
    .sort((a, b) => a.distance - b.distance);
  const [best, second] = distances;
  if (!best || best.distance > MAX_DISTANCE || (second && second.distance - best.distance < MIN_MARGIN)) {
    return null;
  }
  return best.value;
}

/**
 * 패널 이미지 → { teams, players, playersHidden }. 못 읽으면 null.
 * - 첫 숫자 앞의 모르는 덩어리(아이콘 등)는 무시하고, 첫 숫자(1~9)를 남은 팀 수로 본다.
 * - 그 뒤 덩어리가 모두 숫자면 인원(최대 2자리), 하나뿐인 ?면 인원이 가려진 것(playersHidden)이다.
 * - 뒤에 모르는 덩어리가 있으면 인원은 알 수 없음(players: null, playersHidden: false)으로 둔다(한 자리만 읽어 틀리지 않게).
 */
export function readStatus(image, templates) {
  const glyphs = extractGlyphs(image).map((g) => classifyGlyph(g.bits, templates));
  const first = glyphs.findIndex((v) => v !== null && v !== '?' && v !== '0');
  if (first === -1) {
    return null;
  }
  const teams = Number(glyphs[first]);
  const rest = glyphs.slice(first + 1);
  if (rest.length === 1 && rest[0] === '?') {
    return { teams, players: null, playersHidden: true };
  }
  const readable = rest.length >= 1 && rest.length <= 2 && rest.every((v) => v !== null && v !== '?');
  return { teams, players: readable ? Number(rest.join('')) : null, playersHidden: false };
}
